import { CABINET_PATHS } from '@nebalkon-workspace/cabinet-core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AdminBirthdayItem } from '@nebalkon-workspace/admin-api';
import { StaffBirthdaysService } from './staff-birthdays.service';

export type StaffBirthdaysBannerKind = 'employees' | 'clients';

@Component({
  selector: 'lib-staff-birthdays-banner',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, RouterLink],
  templateUrl: './staff-birthdays-banner.component.html',
  styleUrl: './staff-birthdays-banner.component.scss',
})
export class StaffBirthdaysBannerComponent implements OnInit {
  readonly kind = input.required<StaffBirthdaysBannerKind>();

  readonly #cabinetPaths = inject(CABINET_PATHS);
  readonly #birthdays = inject(StaffBirthdaysService);
  readonly #translate = inject(TranslateService);

  protected readonly staffCabinetPath = this.#cabinetPaths.staff;

  protected readonly items = computed(() =>
    this.kind() === 'employees'
      ? this.#birthdays.employees()
      : this.#birthdays.clients(),
  );

  protected readonly titleKey = computed(() =>
    this.kind() === 'employees'
      ? 'STAFF.CABINET.BIRTHDAYS.TITLE_EMPLOYEES'
      : 'STAFF.CABINET.BIRTHDAYS.TITLE_CLIENTS',
  );

  public ngOnInit(): void {
    this.#birthdays.ensureLoaded();
  }

  protected detailLink(item: AdminBirthdayItem): string {
    const segment = this.kind() === 'employees' ? 'employees' : 'users';
    return `${this.staffCabinetPath}/${segment}/${item.user.id}`;
  }

  protected formatName(item: AdminBirthdayItem): string {
    const ind = item.individual;
    const lastName = ind.last_name
      ? ind.last_name.charAt(0).toUpperCase() + ind.last_name.slice(1)
      : '';
    const firstInitial = ind.first_name
      ? `${ind.first_name.charAt(0).toUpperCase()}.`
      : '';
    const patronymicInitial = ind.patronymic
      ? `${ind.patronymic.charAt(0).toUpperCase()}.`
      : '';

    return `${lastName} ${firstInitial}${patronymicInitial}`.trim();
  }

  protected offsetLabel(daysOffset: number): string {
    if (daysOffset === 0) {
      return this.#translate.instant('STAFF.CABINET.BIRTHDAYS.TODAY');
    }
    if (daysOffset === 1) {
      return this.#translate.instant('STAFF.CABINET.BIRTHDAYS.TOMORROW');
    }
    if (daysOffset === -1) {
      return this.#translate.instant('STAFF.CABINET.BIRTHDAYS.YESTERDAY');
    }
    if (daysOffset > 1) {
      return this.#translate.instant('STAFF.CABINET.BIRTHDAYS.IN_DAYS', {
        count: daysOffset,
      });
    }
    return this.#translate.instant('STAFF.CABINET.BIRTHDAYS.DAYS_AGO', {
      count: Math.abs(daysOffset),
    });
  }
}
